import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

interface DeptFile {
  id: string;
  fileName: string;
  status: string;
  versionNumber: number;
}

interface DeptGroup {
  departmentId: string;
  departmentName: string;
  files: DeptFile[];
}

@Component({
  selector: 'app-opendownlodfiles',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './opendownlodfiles.component.html',
  styleUrl: './opendownlodfiles.component.css'
})
export class OpendownlodfilesComponent {
  departments: DeptGroup[] = [];
  selected: Record<string, boolean> = {};
  expanded: Record<string, boolean> = {};

  constructor(
    private readonly dialogRef: MatDialogRef<OpendownlodfilesComponent>,
    @Inject(MAT_DIALOG_DATA) data: { departments: DeptGroup[] }
  ) {
    this.departments = data?.departments ?? [];
    // Pre-select all files and expand every department by default.
    this.departments.forEach(dept => {
      this.expanded[dept.departmentId] = true;
      dept.files.forEach(file => { this.selected[file.id] = true; });
    });
  }

  get totalFiles(): number {
    return this.departments.reduce((count, dept) => count + dept.files.length, 0);
  }

  get selectedCount(): number {
    return Object.values(this.selected).filter(Boolean).length;
  }

  get allSelected(): boolean {
    return this.totalFiles > 0 && this.selectedCount === this.totalFiles;
  }

  get latestOnlySelected(): boolean {
    const latestIds = new Set<string>();

    this.departments.forEach(dept => {
      if (!dept.files.length) { return; }
      const latestVersion = Math.max(...dept.files.map(file => file.versionNumber));
      dept.files.forEach(file => {
        if (file.versionNumber === latestVersion) {
          latestIds.add(file.id);
        }
      });
    });

    if (!latestIds.size) { return false; }

    return Object.keys(this.selected).every(id => !this.selected[id] || latestIds.has(id))
      && Array.from(latestIds).every(id => this.selected[id]);
  }

  isFileSelected(id: string): boolean {
    return !!this.selected[id];
  }

  toggleFile(id: string): void {
    this.selected[id] = !this.selected[id];
  }

  toggleDeptExpand(dept: DeptGroup): void {
    this.expanded[dept.departmentId] = !this.expanded[dept.departmentId];
  }

  isDeptExpanded(dept: DeptGroup): boolean {
    return !!this.expanded[dept.departmentId];
  }

  isDeptAllSelected(dept: DeptGroup): boolean {
    return dept.files.length > 0 && dept.files.every(file => this.selected[file.id]);
  }

  getDeptSelectedCount(dept: DeptGroup): number {
    return dept.files.filter(file => this.selected[file.id]).length;
  }

  toggleDept(dept: DeptGroup, event?: Event): void {
    event?.stopPropagation();
    const shouldSelect = !this.isDeptAllSelected(dept);
    dept.files.forEach(file => { this.selected[file.id] = shouldSelect; });
  }

  toggleAll(): void {
    const shouldSelect = !this.allSelected;
    this.departments.forEach(dept => dept.files.forEach(file => { this.selected[file.id] = shouldSelect; }));
  }

  toggleLatestAll(): void {
    const shouldSelectLatestOnly = !this.latestOnlySelected;
    this.departments.forEach(dept => {
      if (!dept.files.length) { return; }
      const latestVersion = Math.max(...dept.files.map(f => f.versionNumber));
      dept.files.forEach(file => {
        this.selected[file.id] = shouldSelectLatestOnly && file.versionNumber === latestVersion;
      });
    });
  }

  iconType(fileName: string): string {
    const name = (fileName || '').toLowerCase();
    if (name.endsWith('.pdf')) { return 'pdf'; }
    if (name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.xlsm') || name.endsWith('.csv')) { return 'xls'; }
    if (name.endsWith('.doc') || name.endsWith('.docx')) { return 'doc'; }
    if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg')) { return 'image'; }
    return 'default';
  }

  close(): void {
    this.dialogRef.close();
  }

  download(): void {
    const ids = Object.keys(this.selected).filter(id => this.selected[id]);
    if (!ids.length) { return; }
    this.dialogRef.close(ids);
  }
}
